/**
 * Shop the Look Section Script
 * Handles Swiper initialization, video controls, and tab functionality
 */

class ShopTheLook {
  constructor(container) {
    this.container = container;
    this.swipers = {};
    this.currentTab = 0;
    this.currentVideo = null;
    this.isAddingLookToCart = false;
    
    this.init();
  }

  init() {
    this.initMediaSwiper();
    this.initTabsSwiper();
    this.initProductSwipers();
    this.initVideoControls();
    this.initTabNavigation();
    this.initBuyLookButton();
  }

  /**
   * Initialize media (video) swiper
   */
  initMediaSwiper() {
    const mediaSlider = this.container.querySelector('[data-swiper="media"]');
    if (!mediaSlider) {
      console.log('No media slider found');
      return;
    }

    try {
      this.swipers.media = new Swiper(mediaSlider, {
        slidesPerView: 1,
        loop: false,
        allowTouchMove: false, // Controlled by tabs
        on: {
          slideChange: (swiper) => {
            this.handleVideoChange(swiper.activeIndex);
          }
        }
      });
      console.log('Media swiper initialized successfully');
    } catch (error) {
      console.error('Error initializing media swiper:', error);
    }
  }

  /**
   * Initialize tabs navigation swiper
   */
  initTabsSwiper() {
    const tabsSlider = this.container.querySelector('[data-swiper="tabs"]');
    if (!tabsSlider) {
      console.log('No tabs slider found');
      return;
    }

    try {
      this.swipers.tabs = new Swiper(tabsSlider, {
        slidesPerView: 'auto',
        spaceBetween: 16,
        freeMode: true,
        watchSlidesProgress: true
      });
      console.log('Tabs swiper initialized successfully');
    } catch (error) {
      console.error('Error initializing tabs swiper:', error);
    }
  }

  /**
   * Initialize product sliders for each tab
   */
  initProductSwipers() {
    const productSliders = this.container.querySelectorAll('[data-swiper^="products-"]');
    console.log('Found', productSliders.length, 'product sliders');
    
    productSliders.forEach(slider => {
      const swiperId = slider.getAttribute('data-swiper');
      
      try {
        this.swipers[swiperId] = new Swiper(slider, {
          spaceBetween: 16,
          slidesPerView: 1.5,
          navigation: {
            nextEl: slider.querySelector('.swiper-button-next'),
            prevEl: slider.querySelector('.swiper-button-prev'),
          },
          breakpoints: {
            768: {
              spaceBetween: 24,
              slidesPerView: 2.5,
            }
          }
        });
        console.log('Product swiper', swiperId, 'initialized successfully');
      } catch (error) {
        console.error('Error initializing product swiper', swiperId, ':', error);
      }
    });
  }

  /**
   * Initialize video controls (play/pause, mute/unmute)
   */
  initVideoControls() {
    const videos = this.container.querySelectorAll('.shop-the-look__video');
    
    videos.forEach((video, index) => {
      const slide = video.closest('.swiper-slide');
      const playPauseBtn = slide?.querySelector('.play-pause-btn');
      const soundBtn = slide?.querySelector('.sound-btn');

      if (playPauseBtn) {
        playPauseBtn.addEventListener('click', () => {
          this.togglePlayPause(video, playPauseBtn);
        });
      }

      if (soundBtn) {
        soundBtn.addEventListener('click', () => {
          this.toggleSound(video, soundBtn);
        });
      }

      // Set current video if it's the first one
      if (index === 0) {
        this.currentVideo = video;
        this.updatePlayPauseButton(video, playPauseBtn);
      }
    });
  }

  /**
   * Initialize tab navigation functionality
   */
  initTabNavigation() {
    const tabs = this.container.querySelectorAll('.shop-the-look__tab');
    
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => {
        this.switchTab(index);
      });
    });
  }

  /**
   * Switch to a specific tab
   */
  switchTab(tabIndex) {
    const tabs = this.container.querySelectorAll('.shop-the-look__tab');
    const panels = this.container.querySelectorAll('.shop-the-look__panel');
    
    // Update tab active states
    tabs.forEach((tab, index) => {
      tab.classList.toggle('active', index === tabIndex);
    });

    // Update panel active states
    panels.forEach((panel, index) => {
      panel.classList.toggle('active', index === tabIndex);
    });

    // Switch media slide if media swiper exists
    if (this.swipers.media) {
      this.swipers.media.slideTo(tabIndex);
    }

    this.currentTab = tabIndex;
  }

  /**
   * Initialize "Buy the look" button (add all items in active tab to cart)
   */
  initBuyLookButton() {
    const button = this.container.querySelector('.buy-the_look--button');
    if (!button) return;

    // Guard against duplicate initialization (can happen with re-renders/section reloads)
    if (button.dataset.shopTheLookBound === 'true') return;
    button.dataset.shopTheLookBound = 'true';

    button.addEventListener('click', (event) => {
      event.preventDefault();
      this.addActiveLookToCart(button);
    });
  }

  getActivePanel() {
    const panels = this.container.querySelectorAll('.shop-the-look__panel');
    if (!panels || panels.length === 0) return null;
    return panels[this.currentTab] || this.container.querySelector('.shop-the-look__panel.active') || null;
  }

  collectVariantIdsFromPanel(panel) {
    if (!panel) return [];

    const ids = [];
    const seen = new Set();

    // Prefer per-card resolution to avoid grabbing duplicate hidden inputs (e.g. corner ATC)
    const productCards = panel.querySelectorAll('.shop-the-look__product-card, .product-card');
    if (productCards && productCards.length) {
      productCards.forEach((card) => {
        const idEl = card.querySelector(
          'form[data-type="add-to-cart-form"] [name="id"]:not([disabled]), [name="id"]:not([disabled])'
        );
        const value = idEl && idEl.value ? String(idEl.value).trim() : '';
        if (value && !seen.has(value)) {
          seen.add(value);
          ids.push(value);
        }
      });
      return ids;
    }

    // Fallback: collect any enabled variant id fields in the panel
    panel
      .querySelectorAll('form[data-type="add-to-cart-form"] [name="id"]:not([disabled]), [name="id"]:not([disabled])')
      .forEach((el) => {
        const value = el && el.value ? String(el.value).trim() : '';
        if (value && !seen.has(value)) {
          seen.add(value);
          ids.push(value);
        }
      });

    return ids;
  }

  toggleBuyLookLoading(button, loading) {
    if (!button) return;
    button.toggleAttribute('disabled', !!loading);
    button.classList.toggle('is-loading', !!loading);
  }

  async addActiveLookToCart(button) {
    if (this.isAddingLookToCart) return;

    const panel = this.getActivePanel();
    const variantIds = this.collectVariantIdsFromPanel(panel);
    if (!variantIds.length) return;

    this.isAddingLookToCart = true;
    this.toggleBuyLookLoading(button, true);

    const items = { items: variantIds.map((id) => ({ id, quantity: 1 })) };

    try {
      // Use theme's AJAX cart flow when available (keeps cart drawer in sync)
      if (window.FoxTheme?.routes?.cart_add_url && window.FoxTheme?.utils?.fetchConfig) {
        const cartType = window.FoxTheme?.settings?.cartType;
        const isCartTemplate = document.body.classList.contains('cart-template');

        if (isCartTemplate || cartType !== 'drawer') {
          if (window.FoxTheme?.utils?.postLink) {
            window.FoxTheme.utils.postLink(window.FoxTheme.routes.cart_add_url, { parameters: { ...items } });
            return;
          }
          // Fallback redirect if postLink isn't available
          window.location.href = '/cart';
          return;
        }

        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const body = JSON.stringify({
          ...items,
          sections: sectionsToBundle,
          section_url: window.location.pathname,
        });

        const addRes = await fetch(`${window.FoxTheme.routes.cart_add_url}`, {
          ...window.FoxTheme.utils.fetchConfig('javascript'),
          body,
        });
        const parsedState = await addRes.json();

        if (parsedState?.status) {
          alert(parsedState.description || 'Failed to add items to cart.');
          return;
        }

        // Refresh full cart JSON (and keep returned sections)
        const cartJson = await (
          await fetch(`${window.FoxTheme.routes.cart_url}`, { ...window.FoxTheme.utils.fetchConfig() })
        ).json();
        cartJson['sections'] = parsedState['sections'];

        window.FoxTheme?.pubsub?.publish?.(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: parsedState } }));

        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.show === 'function') {
          cartDrawer.show(button);
        }

        return;
      }

      // Generic Shopify fallback
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(items),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => null);
        alert(err?.description || 'Failed to add items to cart.');
        return;
      }

      window.location.href = '/cart';
    } catch (e) {
      console.error(e);
      alert('Failed to add items to cart.');
    } finally {
      this.isAddingLookToCart = false;
      this.toggleBuyLookLoading(button, false);
    }
  }

  /**
   * Handle video change when sliding
   */
  handleVideoChange(slideIndex) {
    const videos = this.container.querySelectorAll('.shop-the-look__video');
    
    // Pause all videos first
    videos.forEach(video => {
      if (!video.paused) {
        video.pause();
      }
    });

    // Play current video
    if (videos[slideIndex]) {
      this.currentVideo = videos[slideIndex];
      this.currentVideo.play().catch(() => {
        // Handle autoplay restrictions
        console.log('Autoplay prevented');
      });
    }
  }

  /**
   * Toggle play/pause for video
   */
  togglePlayPause(video, button) {
    if (video.paused) {
      video.play().then(() => {
        this.updatePlayPauseButton(video, button);
      }).catch(() => {
        console.log('Play failed');
      });
    } else {
      video.pause();
      this.updatePlayPauseButton(video, button);
    }
  }

  /**
   * Update play/pause button icon
   */
  updatePlayPauseButton(video, button) {
    if (!button) return;

    const playIcon = button.querySelector('.icon-play');
    const pauseIcon = button.querySelector('.icon-pause');

    if (video.paused) {
      playIcon.style.display = 'block';
      pauseIcon.style.display = 'none';
      button.setAttribute('aria-label', 'Play');
    } else {
      playIcon.style.display = 'none';
      pauseIcon.style.display = 'block';
      button.setAttribute('aria-label', 'Pause');
    }
  }

  /**
   * Toggle sound for video
   */
  toggleSound(video, button) {
    video.muted = !video.muted;
    this.updateSoundButton(video, button);
  }

  /**
   * Update sound button icon
   */
  updateSoundButton(video, button) {
    if (!button) return;

    const soundOffIcon = button.querySelector('.icon-sound-off');
    const soundOnIcon = button.querySelector('.icon-sound-on');

    if (video.muted) {
      soundOffIcon.style.display = 'block';
      soundOnIcon.style.display = 'none';
      button.setAttribute('aria-label', 'Unmute');
    } else {
      soundOffIcon.style.display = 'none';
      soundOnIcon.style.display = 'block';
      button.setAttribute('aria-label', 'Mute');
    }
  }

  /**
   * Destroy all swipers
   */
  destroy() {
    Object.values(this.swipers).forEach(swiper => {
      if (swiper && swiper.destroy) {
        swiper.destroy(true, true);
      }
    });
    this.swipers = {};
  }
}

/**
 * Initialize Shop the Look sections when DOM is loaded
 */
function initShopTheLook() {
  const sections = document.querySelectorAll('.shop-the-look');
  
  sections.forEach(section => {
    // Check if Swiper is available
    if (typeof Swiper !== 'undefined') {
      if (section._shopTheLookInstance && typeof section._shopTheLookInstance.destroy === 'function') {
        section._shopTheLookInstance.destroy();
      }
      const instance = new ShopTheLook(section);
      section._shopTheLookInstance = instance;
    } else {
      console.error('Swiper library not loaded. Make sure Swiper CSS and JS are included.');
    }
  });
}

/**
 * Handle section load/reload in theme editor
 */
function handleSectionEvents() {
  document.addEventListener('shopify:section:load', function(event) {
    if (event.target.classList.contains('shop-the-look')) {
      if (typeof Swiper !== 'undefined') {
        if (event.target._shopTheLookInstance && typeof event.target._shopTheLookInstance.destroy === 'function') {
          event.target._shopTheLookInstance.destroy();
        }
        const instance = new ShopTheLook(event.target);
        event.target._shopTheLookInstance = instance;
      }
    }
  });

  document.addEventListener('shopify:section:unload', function(event) {
    if (event.target.classList.contains('shop-the-look')) {
      const instance = event.target._shopTheLookInstance;
      if (instance) {
        instance.destroy();
      }
    }
  });
}

/**
 * Wait for Swiper to be loaded, then initialize
 */
function waitForSwiper() {
  if (typeof Swiper !== 'undefined') {
    initShopTheLook();
  } else {
    // If Swiper is not ready, wait a bit and try again
    setTimeout(waitForSwiper, 100);
  }
}

/**
 * Initialize when DOM is ready
 */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', waitForSwiper);
} else {
  waitForSwiper();
}

// Handle section events for theme editor
handleSectionEvents();

// Export for potential external use
window.ShopTheLook = ShopTheLook;
